import React, { useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assessmentsService, ScheduleTemplateSlotInput } from '../api/assessmentsService';

const defaultSlotsExample: ScheduleTemplateSlotInput[] = [
  {
    dayIndex: 0,
    startTime: '10:30',
    endTime: '11:20',
    label: 'Interview #1',
    slotType: 'session',
    visibility: 'assigned',
    sortOrder: 1,
    isInterviewPeriod: true,
    interviewPeriodIndex: 1,
  },
  {
    dayIndex: 0,
    startTime: '11:20',
    endTime: '11:30',
    label: '10 Minute Break',
    slotType: 'break',
    visibility: 'all',
    sortOrder: 2,
  },
  {
    dayIndex: 0,
    startTime: '12:30',
    endTime: '13:15',
    label: 'Lunch',
    slotType: 'meal',
    visibility: 'all',
    sortOrder: 3,
  },
  {
    dayIndex: 1,
    startTime: '08:30',
    endTime: '09:20',
    label: 'Interview #4',
    slotType: 'session',
    visibility: 'assigned',
    sortOrder: 1,
    isInterviewPeriod: true,
    interviewPeriodIndex: 4,
  },
  {
    dayIndex: 1,
    startTime: '09:20',
    endTime: '09:30',
    label: '10 Minute Break',
    slotType: 'break',
    visibility: 'all',
    sortOrder: 2,
  },
];

const ScheduleTemplates: React.FC = () => {
  const queryClient = useQueryClient();
  const templatesQuery = useQuery({
    queryKey: ['admin', 'assessments', 'scheduleTemplates'],
    queryFn: () => assessmentsService.listTemplates(),
  });

  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [slotsJson, setSlotsJson] = useState(JSON.stringify(defaultSlotsExample, null, 2));
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [editJson, setEditJson] = useState<string>('');

  const canCreate = useMemo(() => name.trim().length > 0, [name]);

  const createMutation = useMutation({
    mutationFn: async () => {
      let slots: ScheduleTemplateSlotInput[] | undefined = undefined;
      if (slotsJson.trim().length > 0) {
        slots = JSON.parse(slotsJson);
      }
      return assessmentsService.createTemplate({ name: name.trim(), isDefault, slots });
    },
    onSuccess: async () => {
      setName('');
      setIsDefault(false);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', 'scheduleTemplates'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const tid = parseInt(selectedTemplateId, 10);
      if (!tid) throw new Error('Select a template');
      const parsed = JSON.parse(editJson || '[]') as ScheduleTemplateSlotInput[];
      return assessmentsService.updateTemplate(tid, { slots: parsed });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'assessments', 'scheduleTemplates'] });
    },
  });

  const selectedTemplate = useMemo(() => {
    const tid = parseInt(selectedTemplateId, 10);
    return (templatesQuery.data?.data || []).find((t) => t.id === tid);
  }, [selectedTemplateId, templatesQuery.data]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
          <h1 className="text-2xl font-bold text-gray-900">Schedule Templates</h1>
          <p className="text-gray-600 mt-2">
            Templates are the starting point for new assessment event schedules.
          </p>
        </div>

        <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Create Template</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              className="border border-gray-300 rounded-md px-3 py-2"
              placeholder="Template name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
              Set as default
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slots JSON</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-xs"
              rows={14}
              value={slotsJson}
              onChange={(e) => setSlotsJson(e.target.value)}
            />
            <div className="text-xs text-gray-500 mt-1">
              Each slot needs at least <code>startTime</code> and <code>endTime</code>. Use <code>visibility: "all"</code> for breaks/lunch.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              disabled={!canCreate || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              Create
            </button>
            {createMutation.isError && (
              <div className="text-sm text-red-700">{(createMutation.error as Error).message}</div>
            )}
          </div>
        </div>

        <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Edit Existing Template</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={selectedTemplateId}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedTemplateId(v);
                  const tid = parseInt(v, 10);
                  const t = (templatesQuery.data?.data || []).find((x) => x.id === tid);
                  setEditJson(JSON.stringify(t?.slots || [], null, 2));
                }}
              >
                <option value="">Select…</option>
                {(templatesQuery.data?.data || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.isDefault ? 'Default • ' : ''}{t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedTemplate && (
            <>
              <div className="text-sm text-gray-700">
                Editing <strong>{selectedTemplate.name}</strong>
              </div>
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-xs"
                rows={14}
                value={editJson}
                onChange={(e) => setEditJson(e.target.value)}
              />
              <div className="flex items-center gap-3">
                <button
                  className="inline-flex items-center justify-center px-4 py-2 rounded-md bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                  disabled={updateMutation.isPending}
                  onClick={() => updateMutation.mutate()}
                >
                  Save Slots
                </button>
                {updateMutation.isError && (
                  <div className="text-sm text-red-700">{(updateMutation.error as Error).message}</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ScheduleTemplates;

